import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import axios from "../../config/axiosInstance";
import { ADMIN, CATEGORIES, USERS } from "../../config/apiConfig";
import { useApplication } from "../../contexts/ApplicationContext";

// UI
import { Button, Card, Pagination, Badge, Modal, Table } from "flowbite-react";
import { motion } from "framer-motion";
import { IoMdAdd, IoMdSearch } from "react-icons/io";
import { FiEdit2, FiTrash2, FiRefreshCw, FiFilter, FiX } from "react-icons/fi";
import { MdEmail, MdNumbers } from "react-icons/md";
import { TbLockSearch } from "react-icons/tb";
import { BsFillBarChartFill } from "react-icons/bs";
import { FaUsers } from "react-icons/fa";

// Local components
import UserForm from "../forms/UserForm";
import CardLoading from "../CardLoading";
import ModalDelete from "../modals/ModalDelete";
import EmptyTable from "./EmptyTable";
import TableLoading from "../TableLoading";

/* ============================================================
 * Small helpers
 * ============================================================ */
const useDebounced = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

/* ============================================================
 * Main component
 * ============================================================ */
const UsersTable = () => {
  const { userPermission } = useApplication();
  const queryClient = useQueryClient();

  // UI State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 400);
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | ACTIVE | INACTIVE
  const [roleFilter, setRoleFilter] = useState("ALL"); // ALL | ADMIN | POWER_USER | USER
  const [sortBy, setSortBy] = useState("name_asc"); // name_asc | name_desc | date_desc | date_asc
  const [showFilters, setShowFilters] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [menuSlideIn, setMenuSlideIn] = useState(false);
  const [activeUserCard, setActiveUserCard] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userFormMode, setUserFormMode] = useState("CREATE");

  // Drawers / Modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [isOpenModalDelete, setOpenModalDelete] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const onPageChange = (page) => setCurrentPage(page);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, roleFilter]);

  const ITEMS_PER_PAGE = 10;

  // Users query — hits admin system users view so we can show company + permission data
  const {
    isLoading,
    error,
    data: users,
  } = useQuery(
    ["systemUsers", currentPage, statusFilter, roleFilter],
    async () => {
      const statusParam =
        statusFilter === "ACTIVE"
          ? "1"
          : statusFilter === "INACTIVE"
          ? "0"
          : "";
      const permissionParam = roleFilter === "ALL" ? "" : roleFilter;

      const { data: items } = await axios.get(
        `${ADMIN.GET_SYSTEM_USERS}?page=${currentPage}` +
          `&itemsPerPage=${ITEMS_PER_PAGE}` +
          `${statusParam ? `&status=${statusParam}` : ""}` +
          `${permissionParam ? `&permissionId=${permissionParam}` : ""}`
      );

      setTotalPages(items?.data?.totalPages || 1);
      return items?.data?.data || [];
    },
    { keepPreviousData: true }
  );

  // Optional categories (reserved for future tags/teams)
  const { isLoading: isLoadingCategories, data: categories } = useQuery(
    ["categories"],
    async () => {
      const { data } = await axios.get(`${CATEGORIES.GET_ALL}?page=1`);
      return data?.data?.data || [];
    }
  );

  // Derived/filtered list (client-side)
  const filteredUsers = useMemo(() => {
    let list = Array.isArray(users) ? [...users] : [];

    // search
    const q = (debouncedSearch || "").toLowerCase();
    if (q)
      list = list.filter((u) =>
        `${u?.name || ""} ${u?.email || ""}`.toLowerCase().includes(q)
      );

    // role
    if (roleFilter !== "ALL")
      list = list.filter(
        (u) => (u?.permissionId || "").toUpperCase() === roleFilter
      );

    // sort
    list.sort((a, b) => {
      const aName = (a?.name || "").toLowerCase();
      const bName = (b?.name || "").toLowerCase();
      if (sortBy === "name_asc") return aName.localeCompare(bName);
      if (sortBy === "name_desc") return bName.localeCompare(aName);
      const aDate = new Date(a?.createdAt || 0).getTime();
      const bDate = new Date(b?.createdAt || 0).getTime();
      if (sortBy === "date_desc") return bDate - aDate;
      if (sortBy === "date_asc") return aDate - bDate;
      return 0;
    });

    return list;
  }, [users, debouncedSearch, statusFilter, roleFilter, sortBy]);

  // Actions
  const handleAddUser = () => {
    setSelectedUser(null);
    setUserFormMode("CREATE");
    setIsUserModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserFormMode("EDIT");
    setIsUserModalOpen(true);
  };

  const closeUserModal = () => {
    setIsUserModalOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteAsk = (user) => {
    setSelectedUser(user);
    setOpenModalDelete(true);
  };

  const openUserCardMenu = useCallback((user) => {
    setActiveUserCard(user);
    setUserMenuOpen(true);
    requestAnimationFrame(() => setMenuSlideIn(true));
  }, []);

  const closeUserCardMenu = useCallback(() => {
    setMenuSlideIn(false);
    setTimeout(() => {
      setUserMenuOpen(false);
      setActiveUserCard(null);
    }, 240);
  }, []);

  const deleteUser = async () => {
    try {
      setIsLoadingDelete(true);
      await axios.delete(`${USERS.GET_USER}/${selectedUser.id}`);
      queryClient.invalidateQueries(["users"]);
      setOpenModalDelete(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
    setRoleFilter("ALL");
    setSortBy("name_asc");
  };

  /* ============================================================
   * Layout
   * ============================================================ */
  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-1">
        <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
          Usuários
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Gerenciamento de usuários
            </h1>
          </div>
        </div>
      </header>

      <div className="rounded-2xl border border-gray-200 bg-slate-50 shadow-sm">
        <div className="p-4 space-y-3">
          <form
            className="flex gap-3 md:flex-row md:items-center md:justify-between"
            onSubmit={(e) => {
              e.preventDefault();
              setCurrentPage(1);
            }}
          >
            <div className="flex-1">
              <div className="relative">
                <IoMdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  placeholder="Buscar por nome ou e-mail..."
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 pl-10 text-sm focus:border-gray-300 focus:ring-0"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <Button
              type="submit"
              className="text-white px-1"
              disabled={isLoading}
            >
              Filtrar
            </Button>
          </form>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4">
        <div className="rounded-2xl shadow-sm space-y-4">
          <div className="space-y-3">
            <div className="hidden lg:block overflow-x-auto shadow-sm border border-gray-200 rounded-xl">
              {!isLoading ? (
                filteredUsers?.length ? (
                  <Table striped>
                    <Table.Head>
                      <Table.HeadCell className="text-center bg-gray-100">
                        Código de acesso
                      </Table.HeadCell>
                      <Table.HeadCell className="text-center bg-gray-100">
                        Usuário
                      </Table.HeadCell>
                      <Table.HeadCell className="text-center bg-gray-100">
                        E-mail
                      </Table.HeadCell>
                      <Table.HeadCell className="text-center bg-gray-100">
                        Status
                      </Table.HeadCell>
                      <Table.HeadCell className="text-center bg-gray-100">
                        Ações
                      </Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                      {filteredUsers.map((user, idx) => (
                        <Table.Row key={user?.id || idx} className="bg-white">
                          <Table.Cell className="text-center py-2 font-medium text-gray-900">
                            {user.accessCode || "-"}
                          </Table.Cell>
                          <Table.Cell className="text-center py-2 font-medium text-gray-900">
                            <div>{user.name}</div>
                            <div className="text-2xs -mt-1">
                              Cliente: {user?.Company?.name}
                            </div>
                          </Table.Cell>
                          <Table.Cell className="text-center py-2">
                            {user.email}
                          </Table.Cell>
                          <Table.Cell className="text-center py-2">
                            {user?.isActive ? (
                              <Badge
                                color="success"
                                size="xs"
                                className="w-fit mx-auto"
                              >
                                Ativo
                              </Badge>
                            ) : (
                              <Badge
                                color="failure"
                                size="xs"
                                className="w-fit mx-auto"
                              >
                                Bloqueado
                              </Badge>
                            )}
                          </Table.Cell>
                          <Table.Cell className="text-center py-2">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200"
                                onClick={() => handleEditUser(user)}
                                title="Editar"
                              >
                                <FiEdit2 />
                              </button>
                              {filteredUsers?.length > 1 && (
                                <button
                                  className="p-1.5 rounded-md bg-rose-50 hover:bg-rose-100 text-rose-600"
                                  onClick={() => handleDeleteAsk(user)}
                                  title="Excluir"
                                >
                                  <FiTrash2 />
                                </button>
                              )}
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                ) : (
                  <EmptyTable icon="search_off">
                    Nenhum registro encontrado com esses filtros
                  </EmptyTable>
                )
              ) : (
                <TableLoading cols={6} />
              )}
            </div>

            <div className="lg:hidden space-y-3">
              {!isLoading && filteredUsers?.length ? (
                filteredUsers.map((user, idx) => (
                  <motion.div
                    key={user?.id || idx}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: idx * 0.02 }}
                  >
                    <div
                      className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm cursor-pointer"
                      onClick={() => openUserCardMenu(user)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
                            Código de acesso
                          </p>
                          <p className="text-base font-semibold text-slate-900">
                            {user.accessCode || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="mt-0 grid grid-cols-1 gap-2 text-sm text-slate-600">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                            Usuário
                          </p>
                          <p className="text-sm font-medium text-slate-900">
                            {user.name}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                            E-mail
                          </p>
                          <p className="text-sm text-slate-900">{user.email}</p>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <Badge
                            color={user?.isActive ? "success" : "failure"}
                            size="xs"
                          >
                            {user?.isActive ? "Ativo" : "Bloqueado"}
                          </Badge>
                          <span className="text-[11px] text-slate-500">
                            Criado em:{" "}
                            {new Date(user.createdAt).toLocaleDateString(
                              "pt-BR"
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <>
                  {isLoading ? (
                    Array.from({ length: 3 }).map((_, idx) => (
                      <CardLoading key={idx} />
                    ))
                  ) : (
                    <Card className="rounded-2xl border border-gray-200 bg-white p-4 text-center text-sm text-slate-500">
                      Nenhum usuário encontrado.
                    </Card>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex overflow-x-auto justify-center mt-3">
          <Pagination
            layout="pagination"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            previousLabel=""
            nextLabel=""
            showIcons
          />
        </div>
      )}

      {userMenuOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black opacity-40"
            onClick={closeUserCardMenu}
          />
          <div
            className={`relative w-full rounded-t-xl bg-white px-6 pt-3 pb-8 shadow-[0_0_24px_rgba(15,23,42,0.25)] transition-transform duration-300 ease-out transform ${
              menuSlideIn ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-slate-200" />
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                Ações rápidas
              </p>
              <div className="space-y-1">
                <p className="text-lg font-semibold text-slate-900">
                  {activeUserCard?.name || "Usuário"}
                </p>
                <p className="text-sm text-slate-500">
                  {activeUserCard?.email}
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  color="success"
                  className="rounded-2xl py-3 text-base font-semibold w-full"
                  onClick={() => {
                    if (!activeUserCard) return;
                    handleEditUser(activeUserCard);
                    closeUserCardMenu();
                  }}
                >
                  Editar usuário
                </Button>
                <Button
                  color="failure"
                  className="rounded-2xl py-3 text-base font-semibold w-full"
                  onClick={() => {
                    if (!activeUserCard) return;
                    handleDeleteAsk(activeUserCard);
                    closeUserCardMenu();
                  }}
                >
                  Excluir usuário
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {userMenuOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black opacity-40"
            onClick={closeUserCardMenu}
          />
          <div
            className={`relative w-full rounded-t-xl bg-white px-6 pt-3 pb-8 shadow-[0_0_24px_rgba(15,23,42,0.25)] transition-transform duration-300 ease-out transform ${
              menuSlideIn ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-slate-200" />
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                Ações rápidas
              </p>
              <div className="space-y-1">
                <p className="text-lg font-semibold text-slate-900">
                  {activeUserCard?.name || "Usuário"}
                </p>
                <p className="text-sm text-slate-500">
                  {activeUserCard?.email}
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  color="success"
                  fullWidth
                  className="rounded-2xl py-3 text-base font-semibold w-full"
                  onClick={() => {
                    if (!activeUserCard) return;
                    handleEditUser(activeUserCard);
                    closeUserCardMenu();
                  }}
                >
                  Editar usuário
                </Button>
                <Button
                  color="failure"
                  fullWidth
                  className="rounded-2xl py-3 text-base font-semibold w-full"
                  onClick={() => {
                    if (!activeUserCard) return;
                    handleDeleteAsk(activeUserCard);
                    closeUserCardMenu();
                  }}
                >
                  Excluir usuário
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal show={isUserModalOpen} onClose={closeUserModal} size="4xl">
        <Modal.Header>
          {userFormMode === "CREATE"
            ? "Novo usuário"
            : `Editar usuário: ${selectedUser?.name?.split(" ")[0] || ""}`}
        </Modal.Header>
        <Modal.Body>
          {isUserModalOpen && (
            <UserForm
              user={selectedUser}
              onClose={closeUserModal}
              isOpen={isUserModalOpen}
              type={userFormMode}
              fetchUsers={() => queryClient.invalidateQueries(["users"])}
            />
          )}
        </Modal.Body>
      </Modal>

      <ModalDelete
        handleDelete={deleteUser}
        isOpen={isOpenModalDelete}
        setOpenModal={setOpenModalDelete}
        title="Tem certeza que deseja excluir esse usuário?"
        isLoading={isLoadingDelete}
      />

      {/* Floating Add for mobile */}
      {userPermission !== "USER" && (
        <Button
          className="primary fixed md:hidden bottom-5 right-5 rounded-full h-14 w-14 shadow-lg flex items-center justify-center"
          onClick={handleAddUser}
        >
          <IoMdAdd className="text-2xl" />
        </Button>
      )}
    </div>
  );
};

export default UsersTable;
